<template>
	<div class="filter-box" :class="{ active: userHasSetRange || filterArray.length }">
		<div class="header-toggle" @click="toggleVisibility">
			<a class="pull-right glyphicon toggle-icon" :class="attributeShow[attribute.id] ? 'glyphicon-minus' : 'glyphicon-plus'"/>
			<h3>
				{{attribute.label}}
				<span v-if="attribute.units" class="secondary ml-3">
					({{attribute.units}})
				</span>
			</h3>
		</div>
		<template v-if="attributeShow[attribute.id]">
			<div v-if="attribute.type == 3">
				<div class="price-range-input">
					<input-number
						:min="attribute.min"
						:max="sliderRange.right"
						hide-buttons
						name="slider"
						v-model="sliderRange.left"
						:noFontResize="true"
					/>
					<span class="px-3">-</span>
					<input-number
						:min="sliderRange.left"
						:max="attribute.max"
						hide-buttons
						name="slider"
						v-model="sliderRange.right"
						:noFontResize="true"
					/>
				</div>
				<slider
					v-model="sliderRange"
					:mapping="sliderMapping"
					:leftCap="attribute.min"
					:rightCap="attribute.max"
					:precision="0"
				/>
			</div>
			<div v-else-if="attribute.stock && magicStockInfo(availableStores[0].id)">
				<checkbox
					v-for="(store, index) in (showAll ? availableStores : availableStores.slice(0, showLimit))"
					v-model="attributeFilterMap[attribute.id][store.id]"
					:key="index + '-filter-box-' + availableStores.id"
					:label="store.name"
					:small="isDesktop"
					@input="ev => toggleAttribute(ev, magicStockInfo(store.id))"
					:filterCount="magicStockInfo(store.id).count"
				/>
				<a
					v-if="availableStores.length > showLimit"
					:class="{ small: isDesktop }"
					@click="showAll = ! showAll"
				>
					{{ showAll ? 'Visa färre' : 'Visa fler' }}
				</a>
			</div>
			<div v-else>
				<checkbox
					v-for="(value, index) in (showAll ? attribute.values : attribute.values.slice(0, showLimit))"
					v-model="attributeFilterMap[attribute.id][value.value]"
					:key="index + '-filter-box-' + attribute.id"
					:label="value.label"
					:small="isDesktop"
					@input="ev => toggleAttribute(ev, value)"
					:filterCount="value.count"
				/>
				<a
					v-if="attribute.values.length > showLimit"
					:class="{ small: isDesktop }"
					@click="showAll = ! showAll"
				>
					{{ showAll ? 'Visa färre' : 'Visa fler' }}
				</a>
			</div>
		</template>
		<hr />
	</div>
</template>

<script>
import { linear, cubic } from 'sliderMapping';
import { debounce, isEqual } from 'lodash';
import { mapState } from 'vuex';
import localSessionStorage from 'localSessionStorage';

const ATTRIBUTE_TYPE_CHECKBOX = 1;
const ATTRIBUTE_TYPE_RANGE = 3;

export default {
	props: {
		attribute: {},
		attributeFilterMap: {},
		enabledFilters: {
			type: Array,
			default: null,
		},
		storeValues: {
			type: Array,
			default: null,
		},
		isDesktop: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			sliderRange: {
				left: 0,
				right: 999999,
			},
			showLimit: 5,
			showAll: false,
			applyRangeFilter: true,
			filterArray: [],
			selectedBeyondRange: false,
			setRange: true,
			debounce,
		};
	},
	mounted () {
		this.setFilterArray();
		if (this.attribute.type === ATTRIBUTE_TYPE_RANGE) {
			this.sliderRange.left = this.attribute.selectedMin > this.attribute.min ? this.attribute.selectedMin : this.attribute.min;
			this.sliderRange.right = this.attribute.selectedMax < this.attribute.max ? this.attribute.selectedMax : this.attribute.max;

			this.setRange = this.attribute.selectedMax !== this.attribute.max || this.attribute.selectedMin !== this.attribute.min;

			/** On reload/first load, this checks if previously
			 * selected range is beyond the currently available one,
			 * to make sure it's not overwritten
			*/
			if (this.attribute.selectedMax > this.attribute.max || this.attribute.selectedMin < this.attribute.min) {
				this.selectedBeyondRange = true;
			}
		}
	},
	methods: {
		toggleAttribute (checked, value) {
			let newArray = [...this.filterArray];

			if (checked && newArray.length === 0) {
				// If array is empty, just push checked value
				newArray.push(value.value);
			} else if (checked && newArray.length !== 0) {
				// If array already has items, see if value is already in array
				if (! newArray.some(filterName => filterName === value.value)) {
					newArray.push(value.value);
				}
			} else if (! checked) {
				// If box is unchecked, remove specific value from array
				newArray = newArray.filter(filterName => filterName !== value.value);
			}

			this.filterArray = newArray;
		},
		setFilters () {
			let attributes = null;
			let index;
			let parts;
			let filterAlreadyExists = false;
			const enabledFilters = this.enabledFilters;

			/* Sets new values to range filter */
			if (this.applyRangeFilter && this.attribute.type === ATTRIBUTE_TYPE_RANGE) {
				const attribute = this.attribute;

				attribute.selectedMin = this.sliderRange.left;
				attribute.selectedMax = this.sliderRange.right;

				this.setRange = attribute.selectedMax !== attribute.max || attribute.selectedMin !== attribute.min;
			}

			// Checks if attribute.id is already part of the enabledFilters
			for (let i = 0; i < enabledFilters.length; ++i) {
				if (enabledFilters[i].type === 'attributes') {
					parts = enabledFilters[i].value.split('-');
					if (parts[0] == this.attribute.id) {
						attributes = enabledFilters[i];
						filterAlreadyExists = true;
						index = i;
					}
				}
			}

			// Formats filter for API-call and URL query
			if (this.attribute.type == ATTRIBUTE_TYPE_RANGE) {
				attributes = {type: 'attributes', value: this.attribute.id + '-' + this.attribute.type + '-' + this.attribute.selectedMin + '~' + this.attribute.selectedMax};
			} else  {
				attributes = {type: 'attributes', value: this.attribute.id + '-' + this.attribute.type + '-' + this.filterArray.join('~')};
			}

			// If this attribute.id is already part of enabledFilters we remove it to add correct in next step
			if (filterAlreadyExists) {
				enabledFilters.splice(index, 1);
			}

			// Checks if there are applied filters in array or if slider is not default value with this.setRange
			if (this.filterArray.length !== 0 || (this.attribute.type == ATTRIBUTE_TYPE_RANGE && this.setRange)) {
				enabledFilters.push(attributes);
			}

			if (this.attribute.stock) {
				this.saveSessionStockFilters();
			}
			this.selectedBeyondRange = false;

			this.$emit('updateFilters', enabledFilters);
		},
		toggleVisibility () {
			this.applyRangeFilter = false;
			const updatedShow = this.attributeShow;
			updatedShow[this.attribute.id] = ! updatedShow[this.attribute.id];
		},
		// If the filter type isn't a slider this method adds all checked filters for this.attribute.id
		setFilterArray () {
			const newArray = [];
			if (this.attribute.type !== ATTRIBUTE_TYPE_RANGE) {
				const filters = this.attributeFilterMap[this.attribute.id];
				for (const [key, value] of Object.entries(filters)) {
					if ((value !== null || value !== undefined) && value) {
						newArray.push(key);
					}
				}
			}

			this.filterArray = newArray;
		},
		magicStockInfo (id) {
			// Only using two == because sometimes comparing string to number
			return this.attribute.values.find(element => element.value == id);
		},
		saveSessionStockFilters () {
			localSessionStorage.set('stockFilters', JSON.stringify(this.filterArray));
			if (this.filterArray.length) {
				localSessionStorage.set('stockFilters', JSON.stringify({
					id: this.attribute.id,
					values: this.filterArray,
				}));
			} else {
				localSessionStorage.set('stockFilters', null);
			}
		},
	},
	computed: {
		sliderMapping () {
			return this.attribute.max < 999999 ? linear : cubic;
		},
		// This is used to more easily check updates for min/max of range filters
		availableRange () {
			if (this.attribute.type !== ATTRIBUTE_TYPE_RANGE) {
				return null;
			}

			return {
				min: this.attribute.min,
				max: this.attribute.max,
			};
		},
		userHasSetRange () {
			if (this.attribute.type !== ATTRIBUTE_TYPE_RANGE) {
				return null;
			}
			return this.attribute.selectedMin !== this.attribute.min || this.attribute.selectedMax !== this.attribute.max;
		},
		debouncedUpdateQuery () {
			return debounce(this.setFilters, 250);
		},
		/** If the number of stores returned from backend are
		 * less than all stores, this ensures only available
		 * stores are visible while maintaining sorting
		 */
		availableStores () {
			let availableStores = null;
			if (this.attribute.stock) {
				if (this.storeValues.length !== this.attribute.values.length) {
					availableStores = this.storeValues.reduce((accumulator, availableStore) => {
						if (this.attribute.values.find(value => value.value == availableStore.id)) {
							accumulator.push(availableStore);
						}
						return accumulator;
					}, []);
				} else {
					availableStores = this.storeValues;
				}
			}
			return availableStores;
		},
		...mapState({
			attributeShow: state => state.productFilters.attributeShow,
		}),
	},
	watch: {
		sliderRange: {
			handler () {
				if (this.selectedBeyondRange) {
					this.applyRangeFilter = false;
				} else {
					this.applyRangeFilter = true;
				}
				this.debouncedUpdateQuery();
			},
			deep: true,
		},
		filterArray (to, frm) {
			if (! isEqual(to, frm)) {
				this.setFilters();
			}
		},
		/** If enabledFilters is cleared from parent, this ensures to clear
		 * the local sliderRange aswell as the filterArray if it has a length
		 * (which differs between filter-boxes)
		 */
		enabledFilters (to, frm) {
			if (! to.length && to.length < frm.length) {
				if (this.filterArray.length) {
					this.filterArray = [];
				} else if (this.attribute.type === ATTRIBUTE_TYPE_RANGE) {
					this.sliderRange.left = this.attribute.min;
					this.sliderRange.right = this.attribute.max;
				}
			}
		},
		availableRange (to, frm) {
			// Update slider positions when filter min/max range is updated
			if (! isEqual(to, frm)) {
				/** To be able to save previous slider positions and range filter,
				 * this checks if the selected filter is beyond the current range
				 * and then disables the range filter to update
				 */
				if (this.attribute.selectedMin < this.attribute.min || this.attribute.selectedMax > this.attribute.max) {
					this.selectedBeyondRange = true;
				}

				this.sliderRange.left = this.attribute.selectedMin > this.attribute.min ? this.attribute.selectedMin : this.attribute.min;
				this.sliderRange.right = this.attribute.selectedMax < this.attribute.max ? this.attribute.selectedMax : this.attribute.max;
			}
		},
	},
};
</script>
